import type { Agent } from '@studio/models/agent';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import { Control } from '../control/control.jsx';

/** Props for ControlContent component */
interface ControlContentProps {
  agent?: Agent | undefined;
  className?: string;
}

/**
 * ControlContent component for Iris Connection control settings
 *
 * The Iris Connection → Control sub-tab: the ONLY host of the platform control
 * settings — the connection-type picker, the selected protocol's form (IP,
 * port, VISCA headers, …) and the live PTZ pad under the preview. Per the
 * Command Universe taxonomy (§8 tree, §9 descriptions) its scope is
 * "configuration of the control protocol used by Iris", i.e. the Iris↔camera
 * link, which is why it sits where the user reasons about that link.
 *
 * The top-level Control tab is a different surface, not a superset of this
 * one: it renders the camera's own registry-derived Controls group (Pan Flip,
 * Tilt Flip, native presets, …) and must not repeat the protocol form or the
 * pad — see ../layout/control-section.tsx. "Control" being reachable from two
 * tabs is intentional dual placement; both tabs showing the same protocol
 * fields was the duplication QA caught on the run-5 installer.
 *
 * `<Control>` reads and writes the single zustand `candidate.control` slice via
 * `useStore` / `useProducer`; no local mirror of that state exists here.
 * Per rule: reference-rules-in-comments (ai.knowledge/global) — the judgment
 * above is recorded at the point of need, not only in the commit message.
 */
export const ControlContent = ({ className }: ControlContentProps): ReactElement => {
  return (
    <div className={twMerge('flex flex-col gap-24', className)}>
      <div className='flex flex-col gap-16'>
        <Control className='w-full' />
      </div>
    </div>
  );
};

ControlContent.displayName = 'ControlContent';
